/**
 * Strips markdown formatting from text, leaving only plain text content.
 *
 * Used wherever we need a flat string version of a markdown snippet — speech
 * synthesis (TTS), button `title` attributes, the progress-message title bar,
 * etc. — i.e. anywhere the rendered DOM would be the wrong tool.
 *
 * Strips common block and inline markdown while preserving readable text.
 */
export function stripMarkdown(text: string | undefined | null): string {
  if (!text) return ''

  return (
    text
      // Remove fenced code blocks from spoken/readable text.
      .replace(/```[\s\S]*?```/g, ' ')
      .replace(/~~~[\s\S]*?~~~/g, ' ')
      // Remove images and keep link display text.
      .replace(/!\[[^\]]*]\([^)]*\)/g, ' ')
      .replace(/\[([^\]]+)]\([^)]*\)/g, '$1')
      // Remove blockquote, list, and task-list markers before stripping headers,
      // so a markdown heading like "## 4. Title" keeps the visible number.
      .replace(/^\s*>\s?/gm, '')
      .replace(/^\s*[-*+]\s+\[[ xX]]\s+/gm, '')
      .replace(/^\s*[-*+]\s+/gm, '')
      .replace(/^\s*\d+\.\s+/gm, '')
      // Remove headers (# through ######) at start of lines.
      .replace(/^#{1,6}\s+/gm, '')
      // Remove horizontal rules (--- or em dashes)
      .replace(/^(-{3,}|—{2,})$/gm, '')
      // Remove table separator rows and soften pipe-delimited tables.
      .replace(/^\s*\|?\s*:?-{3,}:?\s*(?:\|\s*:?-{3,}:?\s*)+\|?\s*$/gm, ' ')
      .replace(/\|/g, ' ')
      // Remove inline code markers, preserving the code content.
      .replace(/`([^`]+)`/g, '$1')
      // Remove emphasis markers, preserving content.
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/(^|\s)__([^_\n]*\s[^_\n]*)__(?=\s|$|[.,!?;:])/g, '$1$2')
      .replace(/\*([^*\n]+)\*/g, '$1')
      .replace(/(^|\s)_([^_\n]+)_(?=\s|$|[.,!?;:])/g, '$1$2')
      .replace(/~~(.*?)~~/g, '$1')
      // Replace newlines with spaces
      .replace(/\n+/g, ' ')
      // Collapse multiple spaces
      .replace(/\s+/g, ' ')
      .trim()
  )
}
